import styled from "styled-components";
import { ReactComponent as Trash01 } from "../../../assets/icons/trash-01.svg";
import aiNanaBo from "../../../assets/images/ai-nana-bo.svg";
import aiNanaBoDisabled from "../../../assets/images/ai-nana-bo-disabled.svg";
import ImageLoadingUrl from "../ImageLoadingUrl";
import BtnLargeSolidInverted02 from "../buttons/BtnLargeSolidInverted02";
import CheckboxVector from "../checkbox/CheckboxVector";
import TooltipSimple from "../tooltip/TooltipSimple";
import { formatDefaultDate } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";

export const BUYING_INTEL_EMPTY_VALUE = "--";

// #region SCOPED CSS
const StyledBuyingIntelCard = styled.div`
    &,
    * {
        box-sizing: border-box;
    }

    .buying-intel-card {
        min-width: 0;
        min-height: 460px;
        padding: 8px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        background: var(--backgrounds-lines-n-400, #1e2631);
        border: 1px solid var(--backgrounds-lines-n-300, #1a222d);
        border-radius: 4px;
    }

    .logo-section {
        min-width: 0;
        height: 100px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        background: var(--backgrounds-lines-n-500, #141922);
        border-radius: 4px;
    }

    .brand-logo {
        width: 100%;
        height: 100%;
        min-width: 0;
        padding: 10px 56px;
    }

    .selection-checkbox {
        position: absolute;
        top: 7px;
        left: 7px;
        z-index: 1;
    }

    .card-content {
        min-width: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .identity {
        min-width: 0;
        min-height: 29px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 6px;
    }

    .identity-row,
    .identity-pair {
        min-width: 0;
        display: flex;
        align-items: center;
    }

    .identity-row {
        justify-content: space-between;
        gap: 8px;
    }

    .identity-pair {
        flex: 1;
        gap: 4px;
    }

    .identity-label,
    .identity-value {
        font-family: Roboto, sans-serif;
        font-size: 12px;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.0125em;
        text-transform: uppercase;
        white-space: nowrap;
    }

    .identity-label {
        color: var(--text-icons-gray-100, #d1d1d1);
    }

    .identity-value {
        min-width: 0;
        overflow: hidden;
        color: var(--text-icons-blue-500, #00aeef);
        text-overflow: ellipsis;
    }

    .identity-row.botag-row .identity-label,
    .identity-row.botag-row .identity-value {
        font-size: 12px;
    }

    .remove-button {
        width: 20px;
        height: 20px;
        padding: 2px;
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-icons-gray-300, #4f5b6d);
        background: transparent;
        border: 0;
    }

    .remove-button.enabled {
        color: var(--text-icons-red-500, #ff3636);
        cursor: pointer;
    }

    .remove-button svg {
        width: 16px;
        height: 16px;
    }

    .content-section {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .section-heading {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 10px;
        color: var(--text-icons-gray-100, #d1d1d1);
        font-family: Roboto, sans-serif;
        font-size: 12px;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.0125em;
        text-transform: uppercase;
    }

    .section-heading::after {
        height: 1px;
        flex: 1;
        content: "";
        background: var(--backgrounds-lines-n-100, #3c485a);
    }

    .section-columns {
        min-width: 0;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 24px;
    }

    .data-column {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .column-heading {
        min-height: 10px;
        overflow: hidden;
        color: var(--text-icons-gray-200, #7c868b);
        font-family: Roboto, sans-serif;
        font-size: 12px;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.0125em;
        text-overflow: ellipsis;
        text-transform: uppercase;
        white-space: nowrap;
    }

    .data-row {
        min-width: 0;
        display: grid;
        grid-template-columns: minmax(max-content, 1fr) minmax(0, 1.15fr);
        align-items: center;
        gap: 8px;
        font-family: Roboto, sans-serif;
        font-size: 12px;
        font-weight: 400;
        line-height: normal;
    }

    .data-label {
        min-width: 0;
        overflow: hidden;
        color: var(--text-icons-gray-100, #d1d1d1);
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .data-value {
        min-width: 0;
        overflow: hidden;
        color: var(--text-icons-blue-500, #00aeef);
        text-align: right;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .data-value.success {
        color: var(--text-icons-green-500, #0b0);
    }

    .data-value.warning {
        color: var(--text-icons-orange-500, #f90);
    }

    .data-value.completed {
        color: var(--text-icons-blue-600, #0060cc);
    }

    .data-value.empty {
        color: var(--text-icons-gray-200, #7c868b);
    }

    &.empty-card .identity-label,
    &.empty-card .identity-value,
    &.empty-card .section-heading,
    &.empty-card .column-heading,
    &.empty-card .data-label,
    &.empty-card .data-value {
        color: var(--text-inactive, var(--text-icons-gray-300, #4f5b6d));
    }

    .card-actions {
        min-width: 0;
        margin-top: auto;
        display: grid;
        grid-template-columns: minmax(0, 1fr) 40px minmax(0, 1fr);
        align-items: center;
        gap: 8px;
    }

    .card-action {
        width: 100%;
        min-width: 0;
    }

    .nana-button {
        width: 40px;
        height: 40px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: visible;
        background: transparent;
        border: 0;
    }

    .nana-tooltip-trigger {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .nana-button.enabled {
        cursor: pointer;
    }

    .nana-button:disabled {
        pointer-events: none;
    }

    .nana-button img {
        width: 64px;
        height: 64px;
        max-width: none;
        display: block;
    }

    @media (max-width: 430px) {
        .section-columns {
            gap: 12px;
        }

        .brand-logo {
            padding-right: 32px;
            padding-left: 32px;
        }
    }
`;
// #endregion

/**
 * Custom Buying Intelligence card.
 *
 * This component is intentionally independent from GridCardComponent because the
 * Buying Intelligence design has a different information hierarchy. It receives
 * one raw API item from the standard InfinityGrid and owns its display mapping.
 */
export default function BuyingIntelGridCard({
    className = "",
    data = {},
    emptyCard = false,
    secondaryButtonOnClick,
    thirdButtonOnClick,
    showSelections = true,
    isSelectionsEnabled = false,
    isSelected = false,
    onCheckboxClick
}) {
    // #region DATA
    const cardData = emptyCard ? {} : data || {};
    const {
        ourID,
        buyingCycleID,
        labelBrandName,
        labelBrandLogo,
        botagID,
        productID,
        image,
        colors = [],
        status,
        details = {},
        buyingSummary = {},
        forecastOutcome = {},
        retailBrandName,
        newSeason,
        scope,
        budget,
        currency,
        requestedBy,
        createdAt,
        comparableSeason
    } = cardData;

    const {
        retailBrandName: nestedRetailBrand,
        seasonName: nestedSeason,
        scope: nestedScope,
        budget: nestedBudget,
        requestedBy: nestedRequestedBy,
        requestedOn: nestedRequestedOn,
        comparableSeason: nestedComparableSeason
    } = details || {};
    const { recommendation = {}, committedOrder = {} } = buyingSummary || {};
    const {
        buyingValue: recommendationBuyingValue,
        units: recommendationUnits,
        estimatedSellThrough: recommendationEstimatedSellThrough
    } = recommendation;
    const {
        orderValue: committedOrderValue,
        units: committedOrderUnits,
        estimatedSellThrough: committedOrderEstimatedSellThrough
    } = committedOrder;
    const { recommendation: recommendationForecast = {}, committedOrder: committedOrderForecast = {} } =
        forecastOutcome || {};
    const {
        netTurnover: recommendationNetTurnover,
        grossProfit: recommendationGrossProfit,
        endSeasonStock: recommendationEndSeasonStock
    } = recommendationForecast;
    const {
        netTurnover: committedOrderNetTurnover,
        grossProfit: committedOrderGrossProfit,
        endSeasonStock: committedOrderEndSeasonStock
    } = committedOrderForecast;
    // #endregion

    // #region COMPUTED PROPS
    const cardID = emptyCard ? undefined : (buyingCycleID ?? ourID ?? botagID ?? productID);
    const displayLabelBrandName = emptyCard ? BUYING_INTEL_EMPTY_VALUE : labelBrandName;
    const displayBotagID = emptyCard ? BUYING_INTEL_EMPTY_VALUE : (botagID ?? ourID);
    const displayStatus = emptyCard ? BUYING_INTEL_EMPTY_VALUE : status;
    const logoURL = emptyCard ? undefined : (labelBrandLogo ?? image ?? colors?.[0]?.images?.[0]?.URL);
    const displayRetailBrand = retailBrandName ?? nestedRetailBrand;
    const displaySeason = newSeason ?? nestedSeason;
    const displayScope = scope ?? nestedScope;
    const displayBudget = budget ?? nestedBudget;
    const displayRequestedBy = requestedBy ?? nestedRequestedBy;
    const displayRequestedOn = createdAt ?? nestedRequestedOn;
    const displayComparableSeason = comparableSeason ?? nestedComparableSeason;
    const statusTone = ["completed", "updated"].includes(status?.toLowerCase?.()) ? "completed" : "warning";
    const isCardEnabled = !emptyCard && status?.toLowerCase?.() !== "suspended";
    const actionsEnabled = isCardEnabled;
    const selectionEnabled =
        actionsEnabled &&
        isSelectionsEnabled &&
        typeof onCheckboxClick === "function" &&
        cardID !== null &&
        cardID !== undefined;
    const filesEnabled = actionsEnabled && status?.toLowerCase?.() === "completed";
    const nanaEnabled = actionsEnabled && typeof secondaryButtonOnClick === "function";
    // const dashboardEnabled = actionsEnabled && typeof thirdButtonOnClick === "function";
    const dashboardEnabled = false; // UNTIL "Overview" Page is not implemented

    const detailColumns = [
        {
            rows: [
                { label: "Retail Brand:", value: displayRetailBrand },
                { label: "Season:", value: displaySeason },
                { label: "Scope:", value: displayScope },
                { label: "Budget:", value: toCurrencyDisplayValue(displayBudget, currency) }
            ]
        },
        {
            rows: [
                { label: "Requested by:", value: displayRequestedBy },
                { label: "Requested on:", value: formatDefaultDate(displayRequestedOn) },
                { label: "Comparable season:", value: displayComparableSeason },
                { label: "Status:", value: displayStatus, tone: statusTone }
            ]
        }
    ];

    const buyingSummaryColumns = [
        {
            heading: "RECOMMENDATION",
            rows: [
                { label: "Buying value:", value: recommendationBuyingValue, tone: "success" },
                { label: "Units:", value: recommendationUnits, tone: "success" },
                {
                    label: "Estimated sell-through:",
                    value: recommendationEstimatedSellThrough,
                    tone: "success"
                }
            ]
        },
        {
            heading: "COMMITTED ORDER",
            rows: [
                { label: "Order value:", value: committedOrderValue, tone: "success" },
                { label: "Units:", value: committedOrderUnits, tone: "success" },
                {
                    label: "Estimated sell-through:",
                    value: committedOrderEstimatedSellThrough,
                    tone: "success"
                }
            ]
        }
    ];

    const forecastColumns = [
        {
            heading: "FORECAST OUTCOME - EST.",
            rows: [
                { label: "Net turnover:", value: recommendationNetTurnover, tone: "success" },
                { label: "Gross profit:", value: recommendationGrossProfit, tone: "success" },
                { label: "End-season stock:", value: recommendationEndSeasonStock, tone: "success" }
            ]
        },
        {
            heading: "FORECAST OUTCOME - EST.",
            rows: [
                { label: "Net turnover:", value: committedOrderNetTurnover, tone: "success" },
                { label: "Gross profit:", value: committedOrderGrossProfit, tone: "success" },
                { label: "End-season stock:", value: committedOrderEndSeasonStock, tone: "success" }
            ]
        }
    ];
    // #endregion

    // #region METHODS
    const openMainURL = url => {
        if (!url) {
            Library.showToastMessage({
                type: "warning",
                message: "PDF is not yet generated"
            });
            return;
        }

        window.open(url, "_blank");
    };

    const handleSelectionClick = () => {
        if (selectionEnabled) onCheckboxClick(cardID, !isSelected);
    };
    // #endregion

    // #region RENDER
    return (
        <StyledBuyingIntelCard className={`${className} ${emptyCard ? "empty-card" : ""}`}>
            <article className="buying-intel-card">
                <div className="logo-section">
                    <ImageLoadingUrl
                        className="brand-logo"
                        src={logoURL}
                        alt={`${toDisplayValue(displayLabelBrandName)} logo`}
                        style={{ objectFit: "contain" }}
                    />

                    {showSelections && (
                        <CheckboxVector
                            className="selection-checkbox"
                            disabled={!selectionEnabled}
                            isChecked={isSelected}
                            onCheckboxClick={handleSelectionClick}
                        />
                    )}
                </div>

                <div className="card-content">
                    <div className="identity">
                        <div className="identity-row">
                            <div className="identity-pair">
                                <span className="identity-label">LABEL BRAND:</span>
                                <DisplayValue className="identity-value" value={displayLabelBrandName} />
                            </div>

                            <button
                                type="button"
                                className="remove-button"
                                title="Remove"
                                aria-label="Remove"
                                disabled={true}>
                                <Trash01 />
                            </button>
                        </div>

                        <div className="identity-row botag-row">
                            <div className="identity-pair">
                                <span className="identity-label">BOTAG ID:</span>
                                <DisplayValue className="identity-value" value={displayBotagID} />
                            </div>
                        </div>
                    </div>

                    <CardSection title="DETAILS" columns={detailColumns} />
                    <CardSection title="BUYING SUMMARY" columns={buyingSummaryColumns} />
                    <CardSection columns={forecastColumns} />

                    <div className="card-actions">
                        <BtnLargeSolidInverted02
                            className="card-action"
                            text="FILES"
                            disabled={!filesEnabled}
                            onClick={filesEnabled ? () => openMainURL(data?.mainURL) : undefined}
                        />

                        <TooltipSimple
                            title="Open this Buying Cycle in nanaBO"
                            width="220px"
                            centered={true}
                            disabled={emptyCard}>
                            <span className="nana-tooltip-trigger">
                                <button
                                    type="button"
                                    className={`nana-button ${nanaEnabled ? "enabled" : ""}`}
                                    aria-label="Open this Buying Cycle in nanaBO"
                                    disabled={!nanaEnabled}
                                    onClick={nanaEnabled ? () => secondaryButtonOnClick(data) : undefined}>
                                    <img src={nanaEnabled ? aiNanaBo : aiNanaBoDisabled} alt="" aria-hidden="true" />
                                </button>
                            </span>
                        </TooltipSimple>

                        <BtnLargeSolidInverted02
                            className="card-action"
                            text="VIEW DASHBOARD"
                            disabled={!dashboardEnabled}
                            onClick={dashboardEnabled ? () => thirdButtonOnClick(data) : undefined}
                        />
                    </div>
                </div>
            </article>
        </StyledBuyingIntelCard>
    );
    // #endregion
}

// #region SMALL COMPONENTS
function CardSection({ title, columns = [] }) {
    return (
        <section className="content-section">
            {title && <div className="section-heading">{title}</div>}

            <div className="section-columns">
                {columns.map((column, columnIndex) => (
                    <div className="data-column" key={column?.heading || columnIndex}>
                        {column?.heading && <div className="column-heading">{column.heading}</div>}

                        {column?.rows?.map(row => (
                            <DataRow key={row?.label} label={row?.label} value={row?.value} tone={row?.tone} />
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );
}

function DataRow({ label, value, tone = "" }) {
    return (
        <div className="data-row">
            <span className="data-label" title={label}>
                {label}
            </span>
            <DisplayValue className={`data-value ${tone}`} value={value} />
        </div>
    );
}

function DisplayValue({ className = "", value }) {
    const displayValue = toDisplayValue(value);
    const isEmpty = displayValue === BUYING_INTEL_EMPTY_VALUE;

    return (
        <span className={`${className} ${isEmpty ? "empty" : ""}`} title={String(displayValue)}>
            {displayValue}
        </span>
    );
}

function toDisplayValue(value) {
    if (value === null || value === undefined) return BUYING_INTEL_EMPTY_VALUE;
    if (typeof value === "string") return value.trim() || BUYING_INTEL_EMPTY_VALUE;
    if (typeof value === "number") return Number.isNaN(value) ? BUYING_INTEL_EMPTY_VALUE : value;
    if (typeof value === "boolean") return value ? "Yes" : "No";

    return BUYING_INTEL_EMPTY_VALUE;
}

function toCurrencyDisplayValue(value, currency) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return value;

    const formattedValue = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
    return currency ? `${formattedValue} ${currency}` : formattedValue;
}
// #endregion
